import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";

import React from "react";

import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxHeader,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import { skipToken } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { fontRouter } from "@v2/trpc/routes/font";
import { GOOGLE_FONT_OPTIONS } from "@v2/utils/font";

const ITEM_HEIGHT = 28;

function useFontItems() {
  const projectId = useCurrentProjectId();

  const { data: projectFonts = [] } = fontRouter.getFonts.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const uniqueProjectFonts = React.useMemo(
    () => Array.from(new Set(projectFonts.map((font) => font.name))),
    [projectFonts],
  );

  const projectFontSet = React.useMemo(
    () => new Set(uniqueProjectFonts),
    [uniqueProjectFonts],
  );

  const fontItems: ComboboxItemWithValue[] = React.useMemo(
    () => [
      ...uniqueProjectFonts.map((fontName) => ({
        value: fontName,
        label: fontName,
      })),
      ...GOOGLE_FONT_OPTIONS.map((font) => ({
        value: font.value,
        label: font.label,
      })),
    ],
    [uniqueProjectFonts],
  );

  return { fontItems, projectFontSet };
}

export { useFontItems };

interface FontFamilyComboboxProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: "sm" | "base";
  renderItemLabel?: (item: ComboboxItemWithValue) => React.ReactNode;
}

const FontFamilyCombobox: React.FC<FontFamilyComboboxProps> = ({
  value,
  onChange,
  placeholder,
  size,
  renderItemLabel,
}) => {
  const { fontItems } = useFontItems();
  const [searchValue, setSearchValue] = React.useState("");

  const filteredItems = React.useMemo(() => {
    if (!searchValue) {
      return fontItems;
    }
    const lower = searchValue.toLowerCase().trim();
    return fontItems.filter((item) => {
      return String(item.label).toLowerCase().includes(lower);
    });
  }, [fontItems, searchValue]);

  const [listEl, setListEl] = React.useState<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => listEl,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 10,
  });

  const selectedItem = fontItems.find((item) => item.value === value) ?? null;

  return (
    <ComboboxRoot
      items={filteredItems}
      value={selectedItem}
      onValueChange={(item) => onChange(item?.value ?? "")}
      itemToStringValue={(item) => String(item.label)}
      isItemEqualToValue={(a, b) => a.value === b.value}
      virtualized
      filter={null}
      onInputValueChange={(inputValue) => setSearchValue(inputValue)}
      onItemHighlighted={(_value, details) => {
        if (details.reason === "keyboard") {
          virtualizer.scrollToIndex(details.index, { align: "auto" });
        }
      }}
    >
      <ComboboxTriggerButton
        className="w-full"
        size={size}
        isPlaceholder={!value && Boolean(placeholder)}
      >
        {value || placeholder}
      </ComboboxTriggerButton>
      <ComboboxContent sideOffset={8} align="center">
        <ComboboxHeader>
          <ComboboxInput placeholder="Search fonts" />
        </ComboboxHeader>
        <ComboboxEmpty />
        <ComboboxList ref={setListEl} UNSAFE_className="h-[256px]">
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const item = filteredItems[virtualRow.index];
              if (!item) {
                return null;
              }
              return (
                <ComboboxItem
                  key={item.value}
                  value={item}
                  label={renderItemLabel ? renderItemLabel(item) : item.label}
                  UNSAFE_className="absolute left-0 top-0 w-full"
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                />
              );
            })}
          </div>
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  );
};

export default FontFamilyCombobox;
